// Bounded retry schedule for post-add discount verification: an immediate check,
// then four backing-off retries. Never retries indefinitely - after the last entry,
// the caller must treat the sample as unconfirmed and roll it back.
const SAMPLE_VERIFICATION_RETRY_DELAYS_MS = [0, 200, 400, 800, 1200];

function wait(ms) {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

if (!customElements.get('free-sample-picker')) {
  class FreeSamplePicker extends HTMLElement {
    constructor() {
      super();
      this.selected = new Set();
      this.isBusy = false;
    }

    connectedCallback() {
      this.threshold = Number(this.dataset.threshold || 0);
      this.paidSubtotal = Number(this.dataset.paidSubtotal || 0);
      this.existingCount = Number(this.dataset.existingCount || 0);
      this.maxSamples = Number(this.dataset.maxSamples || 2);
      this.options = [...this.querySelectorAll('[data-sample-option]')];
      this.addButton = this.querySelector('[data-add-samples]');
      this.counter = this.querySelector('[data-sample-counter]');
      this.error = this.querySelector('[data-sample-error]');

      this.options.forEach((option) => {
        if (option.dataset.added !== 'true') {
          option.addEventListener('click', () => this.toggleOption(option));
        }
      });

      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.addSamples());
      }

      // Self-healing: this runs on every re-render (every quantity change, every page
      // load, every drawer refresh), so it continuously corrects a cart that fell below
      // the threshold, or ended up with more sample units than allowed (e.g. a race
      // between two tabs), rather than relying on a single point-in-time check.
      if (this.existingCount > 0) {
        this.reconcile();
      }

      this.updateUi();
    }

    toggleOption(option) {
      if (this.isBusy) return;

      const variantId = option.dataset.variantId;
      if (this.selected.has(variantId)) {
        this.selected.delete(variantId);
        option.classList.remove('is-selected');
        option.setAttribute('aria-pressed', 'false');
      } else {
        const remainingSlots = this.maxSamples - this.existingCount;
        if (this.selected.size >= remainingSlots) return;
        this.selected.add(variantId);
        option.classList.add('is-selected');
        option.setAttribute('aria-pressed', 'true');
      }

      this.updateUi();
    }

    updateUi() {
      const totalSelected = this.existingCount + this.selected.size;
      const remainingSlots = Math.max(this.maxSamples - this.existingCount, 0);

      if (this.counter) this.counter.textContent = `${totalSelected} of ${this.maxSamples}`;

      this.options.forEach((option) => {
        if (option.dataset.added === 'true') return;
        const selected = this.selected.has(option.dataset.variantId);
        option.classList.toggle(
          'is-unavailable-choice',
          !selected && this.selected.size >= remainingSlots && remainingSlots > 0
        );
      });

      if (this.addButton) {
        if (this.existingCount >= this.maxSamples) {
          this.addButton.disabled = true;
          this.addButton.textContent = `${this.maxSamples} samples added`;
        } else {
          this.addButton.disabled = this.selected.size === 0 || this.isBusy;
          this.addButton.textContent = this.isBusy
            ? 'Adding...'
            : this.selected.size === 1
              ? 'Add 1 free fly'
              : 'Add my free flies';
        }
      }
    }

    /**
     * Adds the selected samples, then verifies Shopify actually discounted every sample
     * line in the cart to zero before treating the add as a success. If any sample line
     * isn't free, it's removed immediately and the customer is told clearly - it is never
     * left showing "Free sample" on a line that will actually be charged.
     */
    async addSamples() {
      if (this.isBusy || this.selected.size === 0) return;

      const availableSlots = this.maxSamples - this.existingCount;
      const variantIds = [...this.selected].slice(0, availableSlots);
      if (!variantIds.length) return;

      this.setBusy(true);
      this.setError('');

      try {
        // Re-check the paid subtotal immediately before adding freebies.
        const cart = await this.getCart();
        const paidSubtotal = this.getPaidSubtotal(cart);
        const existingSamples = this.getSampleItems(cart);

        if (paidSubtotal < this.threshold) {
          throw new Error('Your cart no longer qualifies for free samples.');
        }

        const remaining = Math.max(this.maxSamples - this.getSampleQuantity(existingSamples), 0);
        const items = variantIds.slice(0, remaining).map((id) => ({
          id: Number(id),
          quantity: 1,
          properties: {
            _free_sample: 'true'
          }
        }));

        if (!items.length) return;

        const addResponse = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });
        const addData = await addResponse.json();

        if (!addResponse.ok) {
          throw new Error(addData.description || addData.message || theme.strings.cartError);
        }

        // Shopify's automatic discount is the only thing that can make a sample line
        // free, but its allocation can take a moment to be reflected in cart.js right
        // after the add - polling briefly (bounded, never indefinite) avoids treating
        // that normal propagation lag as a real failure.
        const verification = await this.verifySamplesFree();

        if (!verification.confirmed) {
          await this.removeLines(verification.notFree.map((item) => item.key));
          // Refresh first, then announce: refreshing replaces this element (and any
          // error text just set on it) with a freshly rendered instance, so the message
          // must land on the DOM only after that replacement has actually happened.
          await this.refreshCart();
          this.announceCartError(
            "One or more selected samples couldn't be confirmed as free, so we removed them. Please try a different sample, or contact us if this keeps happening."
          );
          return;
        }

        await this.refreshCart();
      } catch (error) {
        const message = error.message || theme.strings.cartError;
        await this.refreshCart().catch(() => {});
        this.announceCartError(message);
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * Self-healing pass. Fetches the live cart and corrects two things a race condition
     * (or a paid item being removed elsewhere) could otherwise leave behind:
     *   1. The paid subtotal has fallen below the threshold - remove every sample line.
     *   2. There are more than `maxSamples` sample units in the cart, or a single sample
     *      line somehow has quantity > 1 - normalize back down to exactly `maxSamples`,
     *      keeping the earliest lines and removing the rest.
     */
    async reconcile() {
      if (this.isBusy) return;
      this.setBusy(true);

      try {
        const cart = await this.getCart();
        const paidSubtotal = this.getPaidSubtotal(cart);
        let sampleItems = this.getSampleItems(cart);

        if (sampleItems.length === 0) return;

        let changed = false;

        if (paidSubtotal < this.threshold) {
          await this.removeLines(sampleItems.map((item) => item.key));
          changed = true;
        } else {
          const overQuantity = sampleItems.filter((item) => item.quantity > 1);
          for (const item of overQuantity) {
            await this.setLineQuantity(item.key, 1);
            changed = true;
          }

          if (changed) {
            sampleItems = this.getSampleItems(await this.getCart());
          }

          const totalUnits = this.getSampleQuantity(sampleItems);
          if (totalUnits > this.maxSamples) {
            let running = 0;
            const toRemove = [];
            sampleItems.forEach((item) => {
              if (running + item.quantity <= this.maxSamples) {
                running += item.quantity;
              } else {
                toRemove.push(item.key);
              }
            });
            if (toRemove.length) {
              await this.removeLines(toRemove);
              changed = true;
            }
          }
        }

        if (changed) {
          await this.refreshCart();
          this.announceCartError("Your free sample selection was adjusted to keep it within this order's limit.");
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * Sets a single cart line's quantity via the key (not the positional line index),
     * which stays correct even if other lines have changed concurrently.
     */
    async setLineQuantity(key, quantity) {
      const response = await fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error(theme.strings.cartError);
      return response.json();
    }

    /**
     * Removes a list of cart lines by key, sequentially.
     */
    async removeLines(keys) {
      for (const key of keys) {
        await this.setLineQuantity(key, 0);
      }
    }

    async getCart() {
      const response = await fetch(`${theme.routes.cart}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(theme.strings.cartError);
      return response.json();
    }

    getSampleItems(cart) {
      return cart.items.filter((item) => item.properties && item.properties._free_sample === 'true');
    }

    getSampleQuantity(items) {
      return items.reduce((total, item) => total + item.quantity, 0);
    }

    /**
     * A sample line is confirmed free either because cart.js already shows it at
     * $0, or because the line's own discount-allocation data already accounts for
     * its full original price (covers the case where final_line_price hasn't been
     * recomputed yet but the discount allocation has already landed).
     */
    isLineConfirmedFree(item) {
      if (item.final_line_price === 0) return true;

      if (typeof item.original_line_price === 'number') {
        if (typeof item.total_discount === 'number' && item.total_discount > 0) {
          return item.total_discount >= item.original_line_price;
        }

        if (Array.isArray(item.discounts) && item.discounts.length > 0) {
          const allocated = item.discounts.reduce((total, discount) => total + (discount.amount || 0), 0);
          return allocated >= item.original_line_price;
        }
      }

      return false;
    }

    /**
     * Polls the cart on a short, bounded schedule (immediate, then four
     * backing-off retries) waiting for Shopify's automatic discount to actually
     * mark every current sample line as free. This exists because the discount
     * allocation can take a moment to be reflected in cart.js right after
     * /cart/add.js responds - a single immediate check can't distinguish that
     * ordinary propagation lag from a genuine failure to discount. Never retries
     * beyond the fixed schedule: if every attempt still finds unconfirmed lines,
     * the caller is expected to roll them back rather than keep waiting.
     */
    async verifySamplesFree() {
      let notFree = [];

      for (const delay of SAMPLE_VERIFICATION_RETRY_DELAYS_MS) {
        await wait(delay);

        const cart = await this.getCart();
        const sampleItems = this.getSampleItems(cart);
        notFree = sampleItems.filter((item) => !this.isLineConfirmedFree(item));

        if (notFree.length === 0) {
          return { confirmed: true };
        }
      }

      return { confirmed: false, notFree };
    }

    getPaidSubtotal(cart) {
      return cart.items.reduce((total, item) => {
        if (item.properties && item.properties._free_sample === 'true') return total;
        return total + item.final_line_price;
      }, 0);
    }

    /**
     * Refreshes the cart. Calls cart-items's own refresh() directly - on both the /cart
     * page and in the drawer - rather than dispatching the theme's generic
     * `dispatch:cart-drawer:refresh` event. cart-items.js's refresh() already accounts
     * for whether it's inside a drawer when deciding which sections to re-render, and,
     * unlike the event-dispatch path, its returned promise only resolves once the DOM
     * has actually been updated. That guarantee is required here: announceCartError()
     * must only run after the refreshed markup (and its own #cart-errors/error slot) is
     * in place, not before.
     */
    async refreshCart() {
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refresh === 'function') {
        await cartItems.refresh();
        // The drawer's own refresh path also recalculates sticky footer heights after
        // a refresh; preserve that here since we're bypassing it.
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      } else {
        window.location.reload();
      }
    }

    /**
     * Shows a customer-facing message using the theme's existing cart error live
     * region (#cart-errors - present in both the cart drawer and the /cart page,
     * already wired with role="alert"), so it survives the cart refresh that
     * immediately precedes it. Must only be called after refreshCart() has resolved.
     */
    announceCartError(message) {
      const cartErrors = document.getElementById('cart-errors');
      if (cartErrors) {
        cartErrors.textContent = message;
        cartErrors.hidden = false;
        return;
      }

      // Fallback if #cart-errors isn't present in this context: use whichever
      // free-sample-picker instance is currently in the document (this element may
      // have been replaced by the refresh above, so re-query rather than using `this`).
      const picker = document.querySelector('free-sample-picker');
      const errorEl = picker ? picker.querySelector('[data-sample-error]') : null;
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
    }

    setBusy(isBusy) {
      this.isBusy = isBusy;
      this.classList.toggle('is-loading', isBusy);
      this.updateUi();
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }

  customElements.define('free-sample-picker', FreeSamplePicker);
}
